package gr.tech.GRAttestation.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.components.inputs.StringInput
import gr.tech.GRAttestation.components.inputs.ValidationTrigger
import gr.tech.GRAttestation.components.layouts.StoreUserPageLayout
import gr.tech.GRAttestation.components.widgets.loadingSpinner
import gr.tech.GRAttestation.infrastructure.api.checkUserResetToken
import gr.tech.GRAttestation.infrastructure.api.storeUserLogout
import gr.tech.GRAttestation.infrastructure.api.userResetPassword
import gr.tech.GRAttestation.infrastructure.session.StoreUserSession
import gr.tech.GRAttestation.infrastructure.validations.validateConfirmPassword
import gr.tech.GRAttestation.infrastructure.validations.validatePassword
import gr.tech.GRAttestation.models.user.auth.PasswordResetModel
import gr.tech.GRAttestation.navigation.SitePage
import gr.tech.GRAttestation.styles.*
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.util.Constants
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page(routeOverride = "reset-password")
@Composable
fun ResetPasswordPage() {
    StoreUserPageLayout(
        title = "Reset Password",
        requiredRoles = emptyList()
    ) {
        ResetPasswordScreen()
    }
}

@Composable
fun ResetPasswordScreen() {
    val scope = rememberCoroutineScope()
    val ctx = rememberPageContext()

    var resetToken by remember { mutableStateOf(ctx.route.params["token"]) }
    var tokenValid by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }

    var submitPressed by remember { mutableStateOf(false) }
    var errorText by remember { mutableStateOf("") }

    var passwordInput by remember { mutableStateOf("") }
    var confirmPasswordInput by remember { mutableStateOf("") }
    var passwordError by remember { mutableStateOf("") }
    var confirmPasswordError by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        resetToken?.let { token ->
            val response = checkUserResetToken(token)
            if (response.success) {
                tokenValid = true
            } else {
                tokenValid = false
                errorText = response.error.message
            }
            loading = false
        } ?: run {
            tokenValid = false
            loading = false
            errorText = "Invalid reset password link. Please try again!"
        }
    }

    fun handleReset() {
        passwordError = validatePassword(passwordInput)
        confirmPasswordError = validateConfirmPassword(passwordInput, confirmPasswordInput)

        if (passwordError.isBlank() && confirmPasswordError.isBlank() && tokenValid) {
            scope.launch {
                submitPressed = true
                errorText = ""

                val response = userResetPassword(
                    PasswordResetModel(
                        newPassword = passwordInput,
                        confirmedPassword = confirmPasswordInput,
                        resetToken = resetToken!!
                    )
                )

                if (response.success) {
                    if (StoreUserSession.isLoggedIn()) storeUserLogout()
                    submitPressed = false
                    ctx.router.navigateTo(SitePage.Login.route)
                } else {
                    errorText = response.error.message
                    submitPressed = false
                }
            }
        }
    }

    Box(
        modifier = AuthBoxStyle.toModifier(),
        contentAlignment = Alignment.Center
    ) {
        Column(modifier = AuthCardStyle.toModifier()) {
            // Card Header
            Column(
                modifier = AuthCardHeaderStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Image(
                    src = "/grlogocenter.png",
                    alt = "Green Rebates Logo",
                    modifier = AuthLogoStyle.toModifier()
                )

                H2(attrs = AuthTitleStyle.toModifier().toAttrs()) {
                    Text("Reset Password")
                }

                P(attrs = AuthSubtitleStyle.toModifier().toAttrs()) {
                    Text("ETO Grow Light Fixture Program 2026")
                }
            }

            // Card Body
            Form(
                attrs = Modifier
                    .fillMaxWidth()
                    .padding(24.px, 26.px)
                    .toAttrs {
                        onSubmit {
                            it.preventDefault()
                            handleReset()
                        }
                    }
            ) {
                when {
                    loading -> {
                        Box(
                            modifier = Modifier.fillMaxWidth().margin(topBottom = 32.px),
                            contentAlignment = Alignment.Center
                        ) {
                            loadingSpinner(modifier = Modifier.color(Theme.DarkGreen.rgb))
                        }
                    }

                    tokenValid -> {
                        StringInput(
                            direction = FlexDirection.Column,
                            inputId = "ResetPasswordInput",
                            labelText = "NEW PASSWORD",
                            value = passwordInput,
                            hasError = passwordError.isNotBlank(),
                            showError = passwordError.isNotBlank(),
                            labelError = passwordError,
                            placeholder = "new password",
                            labelModifier = AuthLabelStyle.toModifier(),
                            inputModifier = AuthInputStyle.toModifier().margin(bottom = 16.px),
                            minLength = Constants.VALIDATIONS.PASSWORD_MIN_LENGTH,
                            maxLength = Constants.VALIDATIONS.PASSWORD_MAX_LENGTH,
                            password = true,
                            validationTrigger = if (passwordError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                            onValueChange = { value ->
                                passwordInput = value
                                if (passwordError.isNotBlank()) passwordError = validatePassword(value)
                                if (confirmPasswordError.isNotBlank()) confirmPasswordError = validateConfirmPassword(value, confirmPasswordInput)
                            }
                        )

                        StringInput(
                            direction = FlexDirection.Column,
                            inputId = "ResetConfirmPasswordInput",
                            labelText = "CONFIRM PASSWORD",
                            value = confirmPasswordInput,
                            hasError = confirmPasswordError.isNotBlank(),
                            showError = confirmPasswordError.isNotBlank(),
                            labelError = confirmPasswordError,
                            placeholder = "confirm password",
                            labelModifier = AuthLabelStyle.toModifier(),
                            inputModifier = AuthInputStyle.toModifier().margin(bottom = 20.px),
                            minLength = Constants.VALIDATIONS.PASSWORD_MIN_LENGTH,
                            maxLength = Constants.VALIDATIONS.PASSWORD_MAX_LENGTH,
                            password = true,
                            validationTrigger = if (confirmPasswordError.isNotBlank()) ValidationTrigger.OnChange else ValidationTrigger.OnBlur,
                            onValueChange = { value ->
                                confirmPasswordInput = value
                                if (confirmPasswordError.isNotBlank()) confirmPasswordError = validateConfirmPassword(passwordInput, value)
                            }
                        )

                        if (errorText.isNotEmpty()) {
                            P(attrs = AuthErrorStyle.toModifier().toAttrs()) {
                                Text(errorText)
                            }
                        }

                        Button(
                            colorPalette = null,
                            enabled = !submitPressed,
                            modifier = AuthButtonStyle.toModifier()
                                .width(100.percent)
                                .padding(12.px),
                            type = ButtonType.Submit,
                            onClick = { }
                        ) {
                            if (submitPressed) {
                                loadingSpinner(modifier = Modifier.color(Colors.White))
                            } else {
                                SpanText(
                                    text = "Reset Password",
                                    modifier = Modifier.fontSize(14.px).fontWeight(600)
                                )
                            }
                        }
                    }

                    else -> {
                        P(attrs = AuthErrorStyle.toModifier().margin(bottom = 20.px).toAttrs()) {
                            Text(errorText.ifEmpty { "Invalid or expired link. Please request a new one." })
                        }

                        Link(
                            text = "Request New Link",
                            path = SitePage.ForgotPassword.route,
                            modifier = Modifier
                                .display(DisplayStyle.Block)
                                .fillMaxWidth()
                                .textAlign(TextAlign.Center)
                                .fontSize(0.75.cssRem)
                                .color(Theme.DarkGreen.rgb)
                        )
                    }
                }

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .margin(top = 20.px),
                    contentAlignment = Alignment.Center
                ) {
                    Link(
                        text = "Back to Login",
                        path = SitePage.Login.route,
                        modifier = Modifier
                            .fontSize(0.75.cssRem)
                            .color(Theme.DarkGreen.rgb)
                    )
                }
            }
        }
    }
}
